import React, { useEffect, useRef, useState } from 'react';
import './IVisaService.css';
import NavBar3 from '../../components/NavBar3/NavBar3';
import Skriptlogo from '../../assets/logos/logo.png';
import HeaderImage from '../../assets/images/iv1.jpeg'; 
import dashboardImage from '../../assets/images/iv2.png'; 
import investmentImage from '../../assets/images/iv3.png'; 
import { motion, useAnimation, useInView } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';

const IVisaService = () => {
  const sphereRef = useRef(null);
  const scrollPos = useRef(0);
  const spherePos = useRef(-50);
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  // State for password popup
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Handle password submission
  const handlePasswordSubmit = () => {
    if (password === 'ivisa@25') {
      setShowPasswordPopup(false);
      setPassword('');
      setPasswordError('');
      // Trigger download
      const link = document.createElement('a');
      link.href = 'https://iwinto.cloud/uploads/IVisaInstaller.exe';
      link.download = 'IVisaInstaller.exe';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  // Handle download button click
  const handleDownloadClick = () => {
    setShowPasswordPopup(true);
    setPassword('');
    setPasswordError('');
  };

  // Close popup
  const closePopup = () => {
    setShowPasswordPopup(false);
    setPassword('');
    setPasswordError('');
  };

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  useEffect(() => {
    const sphere = sphereRef.current;
    sphere.classList.add('animate-in');

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      const scrollDirection = currentScroll > scrollPos.current ? 'down' : 'up';
      scrollPos.current = currentScroll;

      if (scrollDirection === 'down') {
        spherePos.current = Math.min(0, spherePos.current + 0.5);
      } else {
        spherePos.current = Math.max(-100, spherePos.current - 0.5);
      }

      sphere.style.transform = `translateY(${spherePos.current}%)`;
    };

    let ticking = false;
    const optimizedScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', optimizedScroll);
    return () => window.removeEventListener('scroll', optimizedScroll);
  }, []);

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <div className="ivisa-service-page">
      <div className="background-sphere" id="background-sphere" ref={sphereRef}></div>
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          background: { color: { value: "transparent" } },
          fpsLimit: 120,
          interactivity: {
            events: {
              onClick: { enable: true, mode: "push" },
              onHover: { enable: true, mode: "repulse" }
            },
            modes: {
              push: { quantity: 4 },
              repulse: { distance: 100, duration: 0.4 }
            }
          },
          particles: {
            color: { value: ["#FF9000", "#FFD700", "#FFFFFF"] },
            links: {
              color: "#FF9000",
              distance: 150,
              enable: true,
              opacity: 0.3,
              width: 1
            },
            move: {
              direction: "none",
              enable: true,
              outModes: { default: "bounce" },
              random: false,
              speed: 1,
              straight: false
            },
            number: { density: { enable: true }, value: 80 },
            opacity: { value: { min: 0.1, max: 0.5 } },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 3 } }
          },
          detectRetina: true
        }}
      />
      
      <NavBar3 />
      
      <motion.div 
        className="ivisa-hero"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.h1 variants={itemVariants}>
        Visa & Investment Management System
        </motion.h1>
        
        <motion.p className="ivisa-subtitle" variants={itemVariants}>
          Streamline your travel agency operations with our all-in-one solution for visa requirements, 
          citizenship by investment programs, and client management.
        </motion.p>
        
        <motion.div className="platform-buttons" variants={itemVariants}>
          <button 
            className="platform-btn download"
            onClick={handleDownloadClick}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              e.currentTarget.style.setProperty('--x', `${x}%`);
              e.currentTarget.style.setProperty('--y', `${y}%`);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
            </svg>
            Download IVisa for Windows
          </button>
        </motion.div>
      </motion.div>
      
      <motion.div 
        className='ivisa-header-image'
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <img src={HeaderImage} alt="Investment Visa Dashboard"/>
      </motion.div>
      
      <motion.section 
        className="ivisa-overview-section"
        initial="hidden"
        whileInView="visible"
        variants={containerVariants}
        viewport={{ once: true }}
      >
        <motion.div className="ivisa-overview-image" variants={itemVariants}>
          <img src={dashboardImage} alt="Investment Program Dashboard" />
        </motion.div>
        <motion.div className="ivisa-overview-text" variants={itemVariants}>
          <h2 className="ivisa-section-title">Powerful Travel Agency Management</h2>
          <p className="ivisa-section-description">
            Built specifically for travel agencies, our system organizes visa requirements, manages investment programs, 
            and maintains comprehensive client records - all while ensuring your data stays secure and accessible.
          </p>
          <div className="ivisa-feature-tags">
            <motion.div className="ivisa-tag-card" whileHover={{ scale: 1.05 }}>
              <span className="emoji">🔄</span>
              Real-time Sync & Backup
            </motion.div>
            <motion.div className="ivisa-tag-card" whileHover={{ scale: 1.05 }}>
              <span className="emoji">📊</span>
              Excel & PDF Integration
            </motion.div>
            <motion.div className="ivisa-tag-card" whileHover={{ scale: 1.05 }}>
              <span className="emoji">👥</span>
              Client Database & CRM
            </motion.div>
          </div>
        </motion.div>
      </motion.section>
      
      <motion.section 
        className="ivisa-section ivisa-section-features"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.div 
          className="ivisa-section-content"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h2 className="ivisa-section-title">Comprehensive Feature Set</h2>
          <motion.p 
            className="ivisa-section-description"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            From visa requirement management to investment tracking, our platform helps you stay organized 
            and efficient. Automate passport renewal alerts, generate professional reports, and keep your 
            client data secure with cloud backup.
          </motion.p>
          <motion.div 
            className="ivisa-feature-tags"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1, delayChildren: 0.3 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="ivisa-tag-card"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <span className="emoji">📋</span>
              Visa Requirements Manager
            </motion.div>
            <motion.div 
              className="ivisa-tag-card"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <span className="emoji">🔔</span>
              Passport Expiry Alerts
            </motion.div>
            <motion.div 
              className="ivisa-tag-card"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <span className="emoji">💼</span>
              Investment Program Tracking
            </motion.div>
          </motion.div>
        </motion.div>
        <motion.div 
          className="ivisa-section-image"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <img src={investmentImage} alt="Investment Program Features" />
        </motion.div>
      </motion.section>

      {/* Password Popup */}
      {showPasswordPopup && (
        <div className="password-popup-overlay">
          <div className="password-popup">
            <div className="password-popup-header">
              <h3>Enter Password to Download</h3>
              <button 
                className="close-btn"
                onClick={closePopup}
              >
                ×
              </button>
            </div>
            <div className="password-popup-content">
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                autoFocus
              />
              {passwordError && (
                <div className="password-error">{passwordError}</div>
              )}
              <div className="password-popup-buttons">
                <button 
                  className="cancel-btn"
                  onClick={closePopup}
                >
                  Cancel
                </button>
                <button 
                  className="submit-btn"
                  onClick={handlePasswordSubmit}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IVisaService; 